import { motion } from "framer-motion";

export default function WebDevelopment() {
  const container = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
  };

  return (
    <div className="bg-black text-gray-100 overflow-hidden">

      {/* ================= HERO ================= */}
      <section className="relative h-screen flex items-center justify-center text-center">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1800&q=80')",
          }}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2 }}
        />
        <div className="absolute inset-0 bg-black/80" />

        <motion.div
          className="relative z-10 max-w-4xl px-6"
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <h1 className="text-5xl md:text-6xl font-extrabold mb-6">
            Web <span className="text-orange-500">Development</span>
          </h1>
          <p className="text-gray-300 text-lg">
            High-performance websites & scalable web apps built for growth.
          </p>
        </motion.div>
      </section>

      {/* ================= STATS ================= */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-4 gap-8 text-center">
          {[
            { n: "120+", t: "Projects Delivered" },
            { n: "60+", t: "Happy Clients" },
            { n: "5+ Years", t: "Experience" },
            { n: "99%", t: "Retention" },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <h3 className="text-4xl font-extrabold text-orange-500">{s.n}</h3>
              <p className="text-gray-400 mt-2">{s.t}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.h2 className="text-3xl font-bold text-orange-500 mb-6" variants={fadeUp}>
          What We Do
        </motion.h2>
        <motion.p className="text-lg text-gray-300" variants={fadeUp}>
          We design and develop fast, secure and scalable websites using modern
          technologies. From startups to enterprises, we build digital products
          that convert visitors into customers.
        </motion.p>
      </motion.section>

      {/* ================= SERVICES ================= */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            Our Web Solutions
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              "Business Websites",
              "E-commerce Stores",
              "Web Applications",
              "Landing Pages",
            ].map((item, i) => (
              <motion.div
                key={i}
                className="p-8 rounded-2xl shadow bg-gray-900"
                initial={{ opacity: 0, x: i % 2 === 0 ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl font-semibold mb-3">{item}</h3>
                <p className="text-gray-400">
                  Modern, responsive and conversion-optimized {item.toLowerCase()}.
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TECH STACK ================= */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            Technology Stack
          </h2>
          <div className="grid md:grid-cols-6 gap-6 text-center">
            {["React", "Next.js", "Node.js", "MongoDB", "Tailwind", "AWS"].map(
              (tech, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.1 }}
                  className="p-6 bg-black/40 rounded-xl"
                >
                  {tech}
                </motion.div>
              )
            )}
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <motion.section
        className="py-24 max-w-6xl mx-auto px-6"
        variants={container}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.h2 className="text-3xl font-bold text-orange-500 text-center mb-12" variants={fadeUp}>
          Why Choose Us
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8">
          <Feature title="Modern Stack" desc="Future-ready technologies." />
          <Feature title="Responsive Design" desc="Perfect on all devices." />
          <Feature title="SEO Optimized" desc="Fast loading & search friendly." />
        </div>
      </motion.section>

      {/* ================= PROCESS ================= */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            Our Process
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {["Planning", "Design", "Development", "Launch"].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl shadow :bg-gray-900"
              >
                <h3 className="font-semibold mb-2">{step}</h3>
                <p className="text-sm text-gray-400">
                  {step === "Planning" && "Requirements & strategy"}
                  {step === "Design" && "UI/UX & wireframes"}
                  {step === "Development" && "Clean scalable code"}
                  {step === "Launch" && "Testing & deployment"}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      
    

      {/* ================= FAQ ================= */}
      <section className="py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-orange-500 text-center mb-12">
            FAQs
          </h2>
          {[
            "How long does a website take?",
            "Do you provide maintenance?",
            "Is SEO included?",
          ].map((q, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6 p-6 rounded-xl shadow bg-gray-900"
            >
              <h3 className="font-semibold mb-2">{q}</h3>
              <p className="text-gray-400">
                Yes, we provide complete end-to-end solutions.
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= CTA ================= */}
      <motion.section
        className="py-28 text-center bg-gradient-to-r from-orange-600 to-orange-900 text-white"
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-extrabold mb-6">
          Ready to Start Your Project?
        </h2>
        <p className="text-orange-100 mb-10">
          Let’s build something amazing together.
        </p>
        <a
          href="/contact"
          className="inline-block px-12 py-4 rounded-full bg-black text-orange-400 font-semibold"
        >
          Contact Us
        </a>
      </motion.section>
    </div>
  );
}

function Feature({ title, desc }) {
  return (
    <motion.div
      whileHover={{ y: -10 }}
      className="p-8 rounded-3xl shadow bg-gray-900"
    >
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-300">{desc}</p>
    </motion.div>
  );
}
